// On-screen Devanāgarī keyboard. Keys are grouped by independent vowels, the
// mātrā signs (+ anusvāra/visarga/virāma), and consonants by varga. Mātrās and
// the virāma insert as-is so the learner composes each akṣara themselves. All
// glyphs render in font-deva, ≥44px, touch-first. See COURSES-SPEC §3.

interface KeyGroup {
  label: string;
  keys: string[];
}

const GROUPS: KeyGroup[] = [
  {
    label: 'Vowels',
    keys: ['अ', 'आ', 'इ', 'ई', 'उ', 'ऊ', 'ऋ', 'ए', 'ऐ', 'ओ', 'औ'],
  },
  {
    // Dotted-circle shows the bare combining sign; ं ः are anusvāra/visarga, ् is virāma.
    label: 'Mātrās & signs',
    keys: ['ा', 'ि', 'ी', 'ु', 'ू', 'ृ', 'े', 'ै', 'ो', 'ौ', 'ं', 'ः', '्'],
  },
  {
    label: 'Ka–ṅa / Ca–ña',
    keys: ['क', 'ख', 'ग', 'घ', 'ङ', 'च', 'छ', 'ज', 'झ', 'ञ'],
  },
  {
    label: 'Ṭa–ṇa / Ta–na',
    keys: ['ट', 'ठ', 'ड', 'ढ', 'ण', 'त', 'थ', 'द', 'ध', 'न'],
  },
  {
    label: 'Pa–ma / Semivowels',
    keys: ['प', 'फ', 'ब', 'भ', 'म', 'य', 'र', 'ल', 'व'],
  },
  {
    label: 'Sibilants & ha',
    keys: ['श', 'ष', 'स', 'ह', 'ळ', 'ॐ'],
  },
];

export function DevanagariKeyboard({
  onInsert,
  onBackspace,
}: {
  onInsert: (s: string) => void;
  onBackspace: () => void;
}) {
  const keyClass =
    'font-deva min-w-[44px] min-h-[44px] px-2 grid place-items-center rounded-md border border-[var(--color-rule)] ' +
    'bg-[var(--color-vellum)] text-[var(--color-ink)] text-[1.45rem] leading-none ' +
    'hover:border-[var(--color-violet)] hover:bg-[var(--color-vellum-dim)] active:bg-[var(--color-vellum-dim)] ' +
    'transition-colors select-none';

  return (
    <div className="flex flex-col gap-2.5">
      {GROUPS.map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <span className="font-grotesk text-[0.58rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
            {group.label}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {group.keys.map((ch) => (
              <button
                key={ch}
                type="button"
                className={keyClass}
                onClick={() => onInsert(ch)}
                // The combining mātrā signs render with a dotted circle base when standalone.
                aria-label={ch}
              >
                {ch}
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="flex">
        <button
          type="button"
          className={
            'min-w-[64px] min-h-[44px] px-3 grid place-items-center rounded-md border border-[var(--color-rule)] ' +
            'bg-[var(--color-vellum)] text-[var(--color-ink)] font-grotesk text-[0.85rem] ' +
            'hover:border-[var(--color-violet)] hover:bg-[var(--color-vellum-dim)] active:bg-[var(--color-vellum-dim)] ' +
            'transition-colors select-none'
          }
          onClick={onBackspace}
          aria-label="Backspace"
        >
          ⌫
        </button>
      </div>
    </div>
  );
}
